import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid
} from 'recharts';

const riskColor = (level) => ({
  High: 'bg-red-500 text-white', Medium: 'bg-amber-400 text-slate-900', Low: 'bg-emerald-400 text-slate-900',
}[level] || 'bg-slate-100 text-slate-500');

const countColor = (v, max) => {
  if (!v) return 'bg-slate-50 text-slate-300';
  const ratio = max ? v / max : 0;
  if (ratio > 0.66) return 'bg-red-500 text-white';
  if (ratio > 0.33) return 'bg-amber-400 text-slate-900';
  return 'bg-emerald-400 text-slate-900';
};

const HBarChart = ({ data, xKey, yKey, fill, unit = '', testid }) => (
  <div style={{ width: '100%', height: Math.max(180, (data?.length || 1) * 36) }} data-testid={testid}>
    <ResponsiveContainer>
      <BarChart data={data} layout="vertical" margin={{ top: 6, right: 20, left: 10, bottom: 6 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis type="number" stroke="#94a3b8" fontSize={11} unit={unit} />
        <YAxis type="category" dataKey={yKey} stroke="#94a3b8" fontSize={10} width={130} />
        <Tooltip />
        <Bar dataKey={xKey} fill={fill} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const DataTable = ({ columns, rows, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm" data-testid={testid}>
      <thead>
        <tr className="text-xs text-slate-500 border-b border-slate-200">
          {columns.map((c) => <th key={c.key} className={`py-2 px-3 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
            {columns.map((c) => (
              <td key={c.key} className={`py-2 px-3 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.key === columns[0].key ? 'font-medium text-slate-700' : 'text-slate-600'}`}>
                {c.fmt ? c.fmt(row[c.key]) : row[c.key]}
              </td>
            ))}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={columns.length} className="py-6 text-center text-slate-400">No data available</td></tr>}
      </tbody>
    </table>
  </div>
);

const Benchmarking = () => {
  const { getAuthHeaders } = useAuth();
  const [dept, setDept] = useState([]);
  const [appr, setAppr] = useState([]);
  const [vendor, setVendor] = useState([]);
  const [deptRisk, setDeptRisk] = useState([]);
  const [vendorRisk, setVendorRisk] = useState([]);
  const [wfRisk, setWfRisk] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'ReasonDMS | Benchmarking';
    (async () => {
      try {
        const h = getAuthHeaders();
        const [d, a, v, dr, vr, wr] = await Promise.all([
          api.getDepartmentPerformance(h), api.getApprovalEfficiency(h), api.getVendorPerformance(h),
          api.getDeptRiskHeatmap(h), api.getVendorRiskHeatmap(h), api.getWorkflowRiskHeatmap(h),
        ]);
        setDept(d.data || []); setAppr(a.data || []); setVendor(v.data || []);
        setDeptRisk(dr.data || []); setVendorRisk(vr.data || []); setWfRisk(wr.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load benchmarking');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-slate-500" data-testid="benchmarking-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" /> Loading benchmarking…
      </div>
    );
  }

  // Heatmap matrices
  const deptDocTypes = [...new Set(deptRisk.map((r) => r.doc_type))];
  const deptNames = [...new Set(deptRisk.map((r) => r.department))];
  const deptCell = (dep, dt) => {
    const matches = deptRisk.filter((r) => r.department === dep && r.doc_type === dt);
    if (!matches.length) return { count: 0, level: null };
    const top = matches.reduce((a, b) => (b.count > a.count ? b : a));
    const total = matches.reduce((s, m) => s + m.count, 0);
    return { count: total, level: top.risk_level };
  };

  const vendorNames = [...new Set(vendorRisk.map((r) => r.vendor))].slice(0, 20);
  const vendorCats = [...new Set(vendorRisk.map((r) => r.risk_category))];
  const vendorMax = Math.max(1, ...vendorRisk.map((r) => r.count));
  const vendorCell = (vn, cat) => (vendorRisk.find((r) => r.vendor === vn && r.risk_category === cat) || {}).count || 0;

  return (
    <div data-testid="benchmarking-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900">Enterprise Benchmarking</h1>
        <p className="text-sm text-slate-500 mt-0.5">Department, approval, vendor performance &amp; risk heatmaps</p>
      </div>

      <Tabs defaultValue="department" className="w-full">
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="department" data-testid="tab-department">Department</TabsTrigger>
          <TabsTrigger value="approval" data-testid="tab-approval">Approval Efficiency</TabsTrigger>
          <TabsTrigger value="vendor" data-testid="tab-vendor">Vendor</TabsTrigger>
          <TabsTrigger value="heatmaps" data-testid="tab-heatmaps">Heatmaps</TabsTrigger>
        </TabsList>

        <TabsContent value="department" className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Documents by department</CardTitle></CardHeader>
            <CardContent><HBarChart data={dept} xKey="doc_count" yKey="department" fill="#2563eb" testid="chart-dept" /></CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <DataTable testid="table-dept" rows={dept} columns={[
                { key: 'department', label: 'Department' },
                { key: 'doc_count', label: 'Docs', align: 'right' },
                { key: 'avg_risk_score', label: 'Avg Risk', align: 'right' },
                { key: 'high_risk_count', label: 'High Risk', align: 'right' },
                { key: 'avg_workflow_completion_hours', label: 'Avg WF (h)', align: 'right' },
                { key: 'sla_compliance_pct', label: 'SLA %', align: 'right', fmt: (v) => `${v}%` },
                { key: 'compliance_score', label: 'Compliance', align: 'right', fmt: (v) => `${v}%` },
              ]} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approval" className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Avg approval time by department</CardTitle></CardHeader>
            <CardContent><HBarChart data={appr} xKey="avg_approval_hours" yKey="department" fill="#7c3aed" unit="h" testid="chart-approval" /></CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <DataTable testid="table-approval" rows={appr} columns={[
                { key: 'department', label: 'Department' },
                { key: 'avg_approval_hours', label: 'Avg Approval (h)', align: 'right' },
                { key: 'total_approvals', label: 'Approvals', align: 'right' },
                { key: 'overdue_pct', label: 'Overdue %', align: 'right', fmt: (v) => `${v}%` },
              ]} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vendor" className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Documents by vendor</CardTitle></CardHeader>
            <CardContent><HBarChart data={vendor.slice(0, 15)} xKey="doc_count" yKey="vendor" fill="#0d9488" testid="chart-vendor" /></CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <DataTable testid="table-vendor" rows={vendor} columns={[
                { key: 'vendor', label: 'Vendor' },
                { key: 'doc_count', label: 'Docs', align: 'right' },
                { key: 'avg_risk_score', label: 'Avg Risk', align: 'right' },
                { key: 'duplicate_flags', label: 'Duplicates', align: 'right' },
                { key: 'anomaly_count', label: 'Anomalies', align: 'right' },
              ]} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="heatmaps" className="space-y-6" data-testid="heatmaps-content">
          {/* Department risk heatmap */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Department risk heatmap (cell = risk level · count)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid="heatmap-department">
                <table className="border-collapse">
                  <thead>
                    <tr>
                      <th className="text-left text-xs text-slate-500 p-2 sticky left-0 bg-white">Department</th>
                      {deptDocTypes.map((dt) => <th key={dt} className="text-xs text-slate-500 p-2 whitespace-nowrap">{dt}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {deptNames.map((dep) => (
                      <tr key={dep}>
                        <td className="text-xs font-medium text-slate-700 p-2 sticky left-0 bg-white whitespace-nowrap">{dep}</td>
                        {deptDocTypes.map((dt) => {
                          const { count, level } = deptCell(dep, dt);
                          return (
                            <td key={dt} className="p-1">
                              <div className={`min-w-[48px] h-9 rounded flex items-center justify-center text-xs font-semibold ${count ? riskColor(level) : 'bg-slate-50 text-slate-300'}`}>
                                {count || '·'}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {deptNames.length === 0 && <tr><td className="p-4 text-slate-400 text-sm">No data</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Vendor risk heatmap */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Vendor risk heatmap (cell = flag count)</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid="heatmap-vendor">
                {vendorNames.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">No vendor risk flags detected</p>
                ) : (
                  <table className="border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left text-xs text-slate-500 p-2 sticky left-0 bg-white">Vendor</th>
                        {vendorCats.map((c) => <th key={c} className="text-xs text-slate-500 p-2 whitespace-nowrap">{c}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {vendorNames.map((vn) => (
                        <tr key={vn}>
                          <td className="text-xs font-medium text-slate-700 p-2 sticky left-0 bg-white whitespace-nowrap">{vn}</td>
                          {vendorCats.map((c) => {
                            const v = vendorCell(vn, c);
                            return <td key={c} className="p-1"><div className={`min-w-[48px] h-9 rounded flex items-center justify-center text-xs font-semibold ${countColor(v, vendorMax)}`}>{v || '·'}</div></td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Workflow risk heatmap */}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Workflow step risk heatmap</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto" data-testid="heatmap-workflow">
                <table className="border-collapse w-full">
                  <thead>
                    <tr className="text-xs text-slate-500">
                      <th className="text-left p-2">Step</th>
                      <th className="p-2">Overdue count</th>
                      <th className="p-2">Avg hours</th>
                      <th className="p-2">Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wfRisk.map((s) => (
                      <tr key={s.step_name} className="border-b border-slate-50">
                        <td className="text-xs font-medium text-slate-700 p-2">{s.step_name}</td>
                        <td className="p-1"><div className={`h-9 rounded flex items-center justify-center text-xs font-semibold ${s.overdue_count >= 3 ? 'bg-red-500 text-white' : s.overdue_count >= 1 ? 'bg-amber-400 text-slate-900' : 'bg-emerald-400 text-slate-900'}`}>{s.overdue_count}</div></td>
                        <td className="p-1"><div className={`h-9 rounded flex items-center justify-center text-xs font-semibold ${s.avg_hours >= 24 ? 'bg-red-500 text-white' : s.avg_hours >= 8 ? 'bg-amber-400 text-slate-900' : 'bg-emerald-400 text-slate-900'}`}>{s.avg_hours}h</div></td>
                        <td className="p-1"><div className={`h-9 rounded flex items-center justify-center text-xs font-semibold ${riskColor(s.risk)}`}>{s.risk}</div></td>
                      </tr>
                    ))}
                    {wfRisk.length === 0 && <tr><td colSpan={4} className="p-4 text-slate-400 text-sm text-center">No workflow data</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Benchmarking;
